"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { debounce, escapeRegExp } from "lodash";

import TextInput, { TextInputProps } from "@stadium/ui/src/components/form/TextInput";
import Button from "@stadium/ui/src/components/button/button";

import defaultStyles from "./main.module.scss";
import { SelectOptionProps } from "@stadium/ui/src/components/form/SelectDropdown";

const defaultArr = [];

export interface SearchInputProps<P = SelectOptionProps> extends Omit<TextInputProps, "onChange"> {
  data: P[];
  attributes: string[];
  onFilter: (filteredData: P[]) => void;
  /**
   * Resets the search query on change of this boolean to true
   */
  reset?: boolean;
  /**
   * Similar to reset, focus the current search input
   */
  focus?: boolean;
  styles: ModularStyles;
  id: string;
  name: string;
  isSearchClearable?: boolean;
  inputContainerClass?: string;
  useStartWithFilter: boolean;
  addTopAlignClass?: boolean;
  searchGroupIconClass?: string;
  searchIconClass?: string;
}

function SearchInput<P = SelectOptionProps>({
  data = defaultArr,
  attributes = defaultArr,
  onFilter,
  reset,
  focus,
  id,
  name,
  placeholder = "",
  styles = defaultStyles,
  classes = "",
  className = "",
  isSearchClearable = false,
  inputContainerClass = "",
  useStartWithFilter = false,
  addTopAlignClass = false,
  searchGroupIconClass = "",
  searchIconClass = "",
}: SearchInputProps<P>) {
  const [query, setQuery] = useState("");

  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (reset) {
      setQuery("");
    }
  }, [reset]);

  useEffect(() => {
    if (focus) {
      inputRef.current.focus();
    }
  }, [focus]);

  const filterData = useCallback(
    // eslint-disable-next-line react-hooks/use-memo
    debounce(
      (val) => {
        const filteredData = data.filter((obj) => {
          if (useStartWithFilter) {
            // Use startsWith match when useStartWithFilter is provided
            return attributes.some((attr) =>
              String(obj[attr]).toLowerCase().startsWith(val.trim().toLowerCase()),
            );
          } else {
            // Use default regex behavior
            const regexp = new RegExp(escapeRegExp(val.trim()), "i");
            return attributes.some((attr) => regexp.test(obj[attr]));
          }
        });
        onFilter(filteredData);
      },
      300,
      {
        leading: false,
        trailing: true,
      },
    ),
    [onFilter, useStartWithFilter],
  );

  const handleQueryChange = ({ value }) => {
    setQuery(value);
    filterData(value);
  };

  const renderSearchIcon = () => {
    if (!isSearchClearable) return null;
    if (query)
      return (
        <Button
          customClass={`${styles.searchGroupIcon} ${searchGroupIconClass}`}
          onClick={() => handleQueryChange({ value: "" })}
        >
          <i className={styles.clearIcon} />
        </Button>
      );
    return <i className={`${styles.searchIcon} ${searchIconClass}`} />;
  };
  return (
    <div
      className={`${styles.searchGroupwithClear} ${
        addTopAlignClass ? styles.searchTopAlign : ""
      } btm-drawer-fix-height`}
    >
      <TextInput
        id={id}
        name={name}
        value={query}
        onChange={handleQueryChange}
        classes={`${styles.formControlPadding} ${classes}`}
        className={className}
        placeholder={placeholder}
        autoComplete="off"
        inputContainerClass={inputContainerClass}
        ref={inputRef}
        styles={styles !== defaultStyles ? styles : undefined}
      />
      {renderSearchIcon()}
    </div>
  );
}

export default SearchInput;
